import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { html, nothing, type TemplateResult } from 'lit';
import { property } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

import { TimeAxisMath } from '../../core/time-axis-math.js';
import type { GanttZoomLevel } from '../../define.js';
import { todayIndicatorStyle } from './time-axis-css.js';

/**
 * Thin vertical red line marking the current day. Positioned absolutely
 * over the axis + bar container so it paints across every swimlane.
 *
 * The indicator hides itself when "today" is outside the rendered
 * [fromMs, toMs] window so we don't produce an off-viewport render
 * that would force a wider scrollWidth.
 */
export class GanttTodayIndicator extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  @property({ attribute: false })
  accessor zoom: GanttZoomLevel = 'week';

  @property({ attribute: false })
  accessor originMs!: number;

  @property({ attribute: false })
  accessor fromMs!: number;

  @property({ attribute: false })
  accessor toMs!: number;

  /** Injected by tests; defaults to the real clock. */
  @property({ attribute: false })
  accessor now: () => number = () => Date.now();

  override render(): TemplateResult | typeof nothing {
    const todayMs = this.now();
    if (todayMs < this.fromMs || todayMs > this.toMs) return nothing;

    const math = new TimeAxisMath(this.zoom, this.originMs);
    const x = math.dateToX(todayMs) - math.dateToX(this.fromMs);

    return html`
      <div
        class=${todayIndicatorStyle}
        style=${styleMap({ left: `${x}px` })}
      ></div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dv-gantt-today-indicator': GanttTodayIndicator;
  }
}
